import { atom } from 'infrastructure/state/jotai.wrapper'
import {
  StageStatus,
  NodeCorrection,
  PromptsV2Response,
  PromptRunResponse,
  ProcessMode,
  BatchMode,
  StudentFacingOption,
  FilteredPrompt,
  PrepareResultData,
  ProcessResultData,
  ApplyResultData,
} from './types'

export const prepareStatusAtom = atom<StageStatus | null>(null)
export const processStatusAtom = atom<StageStatus | null>(null)
export const applyStatusAtom = atom<StageStatus | null>(null)

export const processModeAtom = atom<ProcessMode>('ai-autocorrect')

export const batchModeAtom = atom<BatchMode>(1)

export const fieldTitleAtom = atom<boolean>(true)
export const fieldDescriptionAtom = atom<boolean>(true)
export const fieldBodyValueAtom = atom<boolean>(true)

export const studentFacingAtom = atom<StudentFacingOption>('all')

export const selectedPromptAtom = atom<FilteredPrompt | null>(null)
export const selectedModelAtom = atom<string>('')
export const selectedProviderAtom = atom<string>('openrouter')

export const pipelineStatusAtom = atom<string>('')
export const pipelineErrorAtom = atom<string | null>(null)

export const prepareResultAtom = atom<PrepareResultData | null>(null)
export const processResultAtom = atom<ProcessResultData | null>(null)
export const selectedNodeResultsAtom = atom<Record<
  string,
  NodeCorrection[]
> | null>(null)
export const applyResultAtom = atom<ApplyResultData | null>(null)

export const copyTreeAtom = atom<boolean>(false)

export const promptsV2Atom = atom<PromptsV2Response['data'] | null>(null)

export const filteredPromptsV2Atom = atom((get) => {
  const promptsV2 = get(promptsV2Atom)

  if (!promptsV2) return []

  const prompts: FilteredPrompt[] = []

  // Always include all prompts from all areas
  Object.entries(promptsV2).forEach(([areaName, areaPrompts]) => {
    Object.entries(areaPrompts).forEach(([promptId, metadata]) => {
      prompts.push({ area: areaName, promptId, metadata })
    })
  })

  // Sort by area, then by promptId
  return prompts.sort((a, b) => {
    if (a.area !== b.area) {
      return a.area.localeCompare(b.area)
    }
    return a.promptId.localeCompare(b.promptId)
  })
})

export const selectedModelValueAtom = atom((get) => {
  const selectedModel = get(selectedModelAtom)
  const selectedPrompt = get(selectedPromptAtom)

  if (selectedModel) {
    return selectedModel
  }

  if (selectedPrompt?.metadata.default_model) {
    return selectedPrompt.metadata.default_model
  }

  return ''
})

export const promptRunResponseAtom = atom<PromptRunResponse | null>(null)
export const promptRunRequestParamsAtom = atom<Record<string, unknown> | null>(
  null,
)
export const promptRunErrorAtom = atom<string | null>(null)
export const promptRunRunningAtom = atom<boolean>(false)

export const promptRunRawResultJsonAtom = atom((get) => {
  const response = get(promptRunResponseAtom)
  const requestParams = get(promptRunRequestParamsAtom)

  if (!response && !requestParams) {
    return ''
  }

  try {
    const rawData = {
      request_parameters: requestParams,
      ...response,
    }
    return JSON.stringify(rawData, null, 2)
  } catch (e) {
    return `Error serializing result: ${e instanceof Error ? e.message : 'Unknown error'}`
  }
})

export const canPrepareStartAtom = atom((get) => {
  const pipelineError = get(pipelineErrorAtom)
  const prepareStatus = get(prepareStatusAtom)

  return pipelineError === null && prepareStatus?.completed !== true
})

export const canProcessStartAtom = atom((get) => {
  const pipelineError = get(pipelineErrorAtom)
  const processStatus = get(processStatusAtom)
  const processMode = get(processModeAtom)
  const selectedPrompt = get(selectedPromptAtom)
  const selectedModelValue = get(selectedModelValueAtom)
  const prepareResult = get(prepareResultAtom)

  // Basic checks: no error, not completed, have prepare results
  if (
    pipelineError !== null ||
    processStatus?.completed === true ||
    prepareResult === null ||
    prepareResult.results?.length === 0 ||
    prepareResult.results?.[0]?.result === undefined
  ) {
    return false
  }

  // If title-autocorrect mode, that's sufficient (as long as process hasn't started)
  if (processMode === 'title-autocorrect') {
    return true
  }

  // For ai-autocorrect mode, need: prompt (which includes area) and model
  return selectedPrompt !== null && selectedModelValue !== ''
})
